import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router-dom';
import { Icon } from '../components/Icon';
import { isActive, type NavGroupDef, type Tint } from './nav';

// Una voce del menu. Nel menu stretto resta solo l'icona, con il nome nel title.
export function NavItem({
  to,
  label,
  icon,
  tint = 'lilac',
  pathname,
  collapsed = false,
  badge,
  onNavigate,
}: {
  to: string;
  label: string;
  icon: string;
  tint?: Tint;
  pathname: string;
  collapsed?: boolean;
  badge?: number;
  onNavigate?: () => void;
}) {
  const active = isActive(to, pathname);
  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      title={collapsed ? label : undefined}
      className="nav-item"
      data-active={active || undefined}
      data-collapsed={collapsed || undefined}
      data-tint={tint}
    >
      <span className="nav-icon" aria-hidden>
        <Icon name={icon} size={17} />
      </span>
      {!collapsed && <span style={{ flex: 1 }}>{label}</span>}
      {badge !== undefined && badge > 0 && <span className="nav-badge">{badge}</span>}
    </NavLink>
  );
}

function Sections({
  def,
  pathname,
  onNavigate,
}: {
  def: NavGroupDef;
  pathname: string;
  onNavigate?: () => void;
}) {
  return (
    <>
      {def.sections.map((s) => (
        <div key={s.label} style={{ display: 'grid', gap: 2 }}>
          <span className="t-overline faint nav-section">{s.label}</span>
          {s.items.map((i) => (
            <NavItem key={i.to} {...i} pathname={pathname} onNavigate={onNavigate} />
          ))}
        </div>
      ))}
    </>
  );
}

// Un gruppo richiudibile (tela, artboard 39). Chiuso con dentro la pagina aperta, il titolo resta
// in evidenza; nel menu stretto è un'icona che apre le sue voci a lato.
export function NavGroup({
  def,
  open,
  onToggle,
  pathname,
  collapsed,
  onNavigate,
}: {
  def: NavGroupDef;
  open: boolean;
  onToggle: () => void;
  pathname: string;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const id = useId();
  const holds = def.sections.some((s) => s.items.some((i) => isActive(i.to, pathname)));
  if (collapsed) return <NavFlyout def={def} holds={holds} pathname={pathname} />;
  return (
    <div>
      <button
        type="button"
        className="nav-group"
        aria-expanded={open}
        aria-controls={id}
        data-holds={holds || undefined}
        onClick={onToggle}
        data-tint={def.tint ?? 'lilac'}
      >
        <span className="nav-icon" aria-hidden>
          <Icon name={def.icon} size={17} />
        </span>
        <span style={{ flex: 1 }}>{def.label}</span>
        <Icon name={open ? 'chevron-down' : 'chevron-right'} size={16} />
      </button>
      {open && (
        <div id={id} className="nav-group-body">
          <Sections def={def} pathname={pathname} onNavigate={onNavigate} />
        </div>
      )}
    </div>
  );
}

function NavFlyout({
  def,
  holds,
  pathname,
}: {
  def: NavGroupDef;
  holds: boolean;
  pathname: string;
}) {
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const id = useId();

  const show = () => {
    window.clearTimeout(timer.current);
    // l'interfaccia è scalata con --zoom: le coordinate dello schermo vanno riportate in scala
    const zoom =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--zoom')) || 1;
    const r = button.current?.getBoundingClientRect();
    if (r) setAt({ top: r.top / zoom, left: r.right / zoom + 8 });
  };
  const hide = () => {
    timer.current = window.setTimeout(() => setAt(null), 150);
  };
  useEffect(() => {
    if (!at) return undefined;
    const outside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!panel.current?.contains(t) && !button.current?.contains(t)) setAt(null);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setAt(null);
        button.current?.focus();
      }
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', esc);
    };
  }, [at]);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  return (
    <div onMouseEnter={show} onMouseLeave={hide}>
      <button
        ref={button}
        type="button"
        className="nav-item"
        data-collapsed
        data-active={holds || undefined}
        aria-label={def.label}
        aria-expanded={at !== null}
        aria-controls={id}
        onClick={() => (at ? setAt(null) : show())}
      >
        <Icon name={def.icon} size={18} />
      </button>
      {/* nel body: il contenuto della pagina non lo copre, e il menu non lo taglia */}
      {at &&
        createPortal(
          <div
            ref={panel}
            id={id}
            className="nav-flyout"
            style={{ top: at.top, left: at.left }}
            onMouseEnter={show}
            onMouseLeave={hide}
          >
            <span className="t-strong" style={{ padding: '4px 10px 2px' }}>
              {def.label}
            </span>
            <Sections def={def} pathname={pathname} onNavigate={() => setAt(null)} />
          </div>,
          document.body,
        )}
    </div>
  );
}
